import Button from '#components/Button/Button';
import { Modal } from '#components/Modal/Modal';
import { THField } from '#components/THField/THField';
import {
  $createLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { normalizeUrl } from '@repo/utils';
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
} from 'lexical';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styles from './InsertLinkModal.module.css';

type Props = {
  isOpen: boolean;
  onClose: VoidFunction;
};

type FormValues = {
  url: string;
  title: string;
};

export default function InsertLinkModal({ isOpen, onClose }: Props) {
  const [editor] = useLexicalComposerContext();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      url: '',
      title: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const selectedText = selection.getTextContent();
          setValue('title', selectedText);

          // Check if the selection is within a link node
          const node = selection.anchor.getNode();

          if ($isLinkNode(node)) {
            setValue('url', node.getURL());
          } else {
            const parent = node.getParent();
            if (parent && $isLinkNode(parent)) {
              setValue('url', parent.getURL());
            }
          }
        }
      });
    }
  }, [isOpen, editor, setValue]);

  const onSubmit = (data: FormValues) => {
    const normalizedUrl = normalizeUrl(data.url);

    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        if (selection.isCollapsed()) {
          // No text selected - insert new link with title
          const linkNode = $createLinkNode(normalizedUrl);
          const textNode = $createTextNode(data.title);
          linkNode.append(textNode);
          $insertNodes([linkNode]);
        } else {
          // Text is selected - use TOGGLE_LINK_COMMAND
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedUrl);
        }
      }
    });
    handleClose();
  };

  function handleClose() {
    onClose();
    reset();
  }

  return (
    <Modal showHeader title='Insert Link' isOpen={isOpen} onClose={handleClose}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <THField
          {...register('url', { required: 'URL is required' })}
          label='URL'
          type='text'
          error={errors.url}
          placeholder='e.g. https://www.domain.com'
        />
        <THField
          {...register('title', { required: 'Title is required' })}
          label='Title'
          type='text'
          error={errors.title}
          placeholder='e.g. Domain'
        />
        <Button type='submit' color='purple' className={styles.button}>
          Insert
        </Button>
      </form>
    </Modal>
  );
}
